import { Box, SimpleGrid, Stack, useBreakpointValue } from '@chakra-ui/react';
import Image from '../Image';
import FullWidthBlock from '../FullWidthBlock';
import { CoreBlock } from '../../types';
import { BlocksProvider } from '../../lib/blocks.context';

interface MediaTextProps {
  block: CoreBlock & {
    attrs: CoreBlock['attrs'] & {
      mediaUrl?: string;
      mediaType?: string;
      mediaPosition?: 'left' | 'right';
    };
  };
  renderBlocks: (block: CoreBlock, i: number) => JSX.Element;
}

const CoreMediaText: React.FC<MediaTextProps> = ({ block, renderBlocks }) => {
  const isTablet = useBreakpointValue({ md: true });

  const {
    align,
    mediaUrl,
    mediaType = 'image',
    mediaPosition = 'left',
    mediaOriginalWidth,
    mediaOriginalHeight,
    imageFill,
    focalPoint,
    verticalAlignment = 'center',
    backgroundColor,
    heroSelectOption,
  } = block.attrs || {};
  const isHero = heroSelectOption === 'is-hero';

  const isImageLeft = mediaPosition === 'left';

  const alignItemsMap = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  };

  const media =
    // eslint-disable-next-line no-nested-ternary
    mediaType === 'image' && mediaUrl ? (
      imageFill ? (
        <Box
          position="relative"
          width="100%"
          height={['300px', null, '100%']}
          overflow="hidden"
          {...(backgroundColor
            ? {
                borderTopRadius: 'lg',
                borderBottomRadius: 'none',
                borderBottomRightRadius: [
                  'none',
                  null,
                  isImageLeft ? 'none' : 'lg',
                ],
                borderBottomLeftRadius: [
                  'none',
                  null,
                  isImageLeft ? 'lg' : 'none',
                ],
                borderTopRightRadius: ['lg', null, isImageLeft ? 'none' : 'lg'],
                borderTopLeftRadius: ['lg', null, isImageLeft ? 'lg' : 'none'],
              }
            : {
                borderRadius: 'lg',
              })}
        >
          <Image
            src={mediaUrl}
            alt=""
            objectFit="cover"
            layout="fill"
            {...(backgroundColor
              ? {
                  borderTopRadius: 'lg',
                  borderBottomRadius: 'none',
                  borderBottomRightRadius: [
                    'none',
                    null,
                    isImageLeft ? 'none' : 'lg',
                  ],
                  borderBottomLeftRadius: [
                    'none',
                    null,
                    isImageLeft ? 'lg' : 'none',
                  ],
                  borderTopRightRadius: [
                    'lg',
                    null,
                    isImageLeft ? 'none' : 'lg',
                  ],
                  borderTopLeftRadius: [
                    'lg',
                    null,
                    isImageLeft ? 'lg' : 'none',
                  ],
                }
              : {
                  borderRadius: 'lg',
                })}
            objectPosition={
              focalPoint
                ? `${focalPoint.x * 100}% ${focalPoint.y * 100}%`
                : 'center'
            }
          />
        </Box>
      ) : (
        <Image
          src={mediaUrl}
          alt=""
          objectFit="cover"
          layout={imageFill ? 'fill' : 'responsive'}
          width={mediaOriginalWidth ?? ''}
          height={mediaOriginalHeight ?? ''}
          {...(backgroundColor
            ? {
                borderTopRadius: 'lg',
                borderBottomRadius: 'none',
                borderBottomRightRadius: [
                  'none',
                  null,
                  isImageLeft ? 'none' : 'lg',
                ],
                borderBottomLeftRadius: [
                  'none',
                  null,
                  isImageLeft ? 'lg' : 'none',
                ],
                borderTopRightRadius: ['lg', null, isImageLeft ? 'none' : 'lg'],
                borderTopLeftRadius: ['lg', null, isImageLeft ? 'lg' : 'none'],
              }
            : {
                borderRadius: 'lg',
              })}
        />
      )
    ) : (
      <Box bg="gray.100" p={4}>
        Unsupported media
      </Box>
    );

  const Wrapper = align === 'wide' ? FullWidthBlock : Box;

  return (
    <BlocksProvider
      value={{
        layoutType: 'section',
        hero: { isHero, heroType: 'media-text' },
      }}
    >
      <Wrapper
        mb={align === 'wide' ? 8 : 0}
        px={align === 'wide' ? 10 : 0}
        data-test-id="media-text"
      >
        <SimpleGrid
          maxW="container.xl"
          marginX="auto"
          columns={{ base: 1, md: 2 }}
          spacing={0}
          alignItems={alignItemsMap[verticalAlignment] || 'center'}
          bg={backgroundColor ? 'white' : ''}
          borderRadius="xl"
          mt={isHero ? 8 : 0}
        >
          {isImageLeft && media}
          <Stack
            spacing={4}
            p={[6, null, null, 8]}
            order={!isImageLeft && isTablet ? 0 : 1}
          >
            {block.innerBlocks?.map(renderBlocks)}
          </Stack>
          {!isImageLeft && media}
        </SimpleGrid>
      </Wrapper>
    </BlocksProvider>
  );
};

export default CoreMediaText;
